import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import Input from "components/Input";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect } from "react";
import { IStatus } from "dto/IStatus";

import CloseIcon from "assets/icons/close.svg?react";
import classNames from "classnames";
import {
  faCaretDown,
  faCaretUp,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import Avatar from "components/Avatar";
import { motion, AnimatePresence } from "framer-motion";
import adminService from "api/adminService";
import { useTranslation } from "react-i18next";

const Accordion = ({ expanded, setExpanded, children }) => {
  return (
    <>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.section
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: "auto" },
              collapsed: { opacity: 0, height: 0 },
            }}
          >
            {children}
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
};

const MetricModal: FC<Props> = ({
  toggle,
  addressBookVerificationId,
  statuses,
}) => {
  const { t } = useTranslation();
  const { data: verificationData } =
    adminService.useGetAddressBookVerificationDetails(
      addressBookVerificationId
    );

  const [, setStatus] = useState<IStatus | null>(null);

  const [isDetailsExpanded, setIsDetailsExpanded] = useState<boolean>(false);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState<boolean>(false);
  const [isEvidencesExpanded, setIsEvidencesExpanded] =
    useState<boolean>(false);

  useEffect(() => {
    if (verificationData?.data.StatusId) {
      const status = statuses.find(
        (s) => s.Id === verificationData.data.StatusId
      );
      if (status) {
        setStatus(status);
      }
    } else {
      setStatus(null);
    }
  }, [verificationData]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>
        {t("metric.title")}
        <CloseIcon
          onClick={() => {
            toggle(false);
          }}
          className={styles.closeIcon}
        />
      </div>

      {verificationData?.data && (
        <div className={styles.inputs}>
          <Input
            disabled={true}
            placeholder={t("forms.nip")}
            onChange={(tt) => {}}
            label={t("forms.nip")}
            value={verificationData?.data?.Nip}
          />
          <Input
            disabled={true}
            placeholder={t("forms.companyName")}
            onChange={(tt) => {}}
            label={t("forms.companyName")}
            value={verificationData?.data?.CompanyName}
          />
          <Input
            disabled={true}
            placeholder={t("forms.regon")}
            onChange={(tt) => {}}
            label={t("forms.regon")}
            value={verificationData?.data?.Regon}
          />
          <Input
            disabled={true}
            placeholder={t("forms.krs")}
            onChange={(tt) => {}}
            label={t("forms.krs")}
            value={verificationData?.data?.Krs}
          />
        </div>
      )}

      <div className={styles.history}>
        <div className={styles.header}>
          {t("admin.applicantData")}
          <div
            onClick={(e) => {
              setIsDetailsExpanded(!isDetailsExpanded);
              e.stopPropagation();
            }}
            className={styles.expanderWrapper}
          >
            <FontAwesomeIcon
              className={classNames(styles.expander)}
              icon={isDetailsExpanded ? faCaretDown : faCaretUp}
            />
          </div>
        </div>
        {isDetailsExpanded && (
          <div className={styles.inputs}>
            <Input
              disabled={true}
              value={verificationData?.data.FirstName}
              RightIcon={
                <div
                  className={classNames(styles.verification, styles.verified)}
                >
                  <FontAwesomeIcon icon={faCheck} />
                </div>
              }
              label={t("forms.firstName")}
              onChange={(tt) => {}}
            ></Input>
            <Input
              disabled={true}
              value={verificationData?.data?.LastName}
              RightIcon={
                <div
                  className={classNames(styles.verification, styles.verified)}
                >
                  <FontAwesomeIcon icon={faCheck} />
                </div>
              }
              label={t("forms.lastName")}
              onChange={(tt) => {}}
            ></Input>
            <Input
              disabled={true}
              value={verificationData.data?.Pesel}
              RightIcon={
                <div
                  className={classNames(styles.verification, styles.verified)}
                >
                  <FontAwesomeIcon icon={faCheck} />
                </div>
              }
              label={t("forms.pesel")}
              onChange={(tt) => {}}
            ></Input>
          </div>
        )}
      </div>

      <div className={styles.history}>
        <div className={styles.header}>
          {t("admin.applicationHistory")}
          <div
            onClick={(e) => {
              setIsHistoryExpanded(!isHistoryExpanded);
              e.stopPropagation();
            }}
            className={styles.expanderWrapper}
          >
            <FontAwesomeIcon
              className={classNames(styles.expander)}
              icon={isHistoryExpanded ? faCaretDown : faCaretUp}
            />
          </div>
        </div>
        <Accordion
          expanded={isHistoryExpanded}
          setExpanded={setIsHistoryExpanded}
        >
          <div className={styles.list}>
            <div className={styles.box}>
              <div className={styles.boxWrapper}>
                <div className={styles.line}></div>
                <Avatar
                  name={`${verificationData?.data?.FirstName} ${verificationData?.data.LastName}`}
                  className={styles.avatar}
                  size="small"
                ></Avatar>

                <div className={styles.boxContent}>
                  <div
                    className={styles.r1}
                  >{`${verificationData?.data?.FirstName} ${verificationData?.data.LastName}`}</div>
                  <div className={styles.r2}>
                    <div className={styles.desc}>
                      {t("admin.applicationSubmitted")}
                    </div>
                    <div className={styles.date}>
                      {moment(verificationData?.data.CreationDate).format(
                        "DD.MM.yyyy | HH:mm"
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {verificationData?.data?.ApprovedBy && (
                <div className={styles.boxWrapper}>
                  <div className={styles.line}></div>
                  <Avatar
                    name={verificationData.data.ApprovedBy}
                    className={styles.avatar}
                    size="small"
                  ></Avatar>

                  <div className={styles.boxContent}>
                    <div className={styles.r1}>
                      {verificationData.data.ApprovedBy}
                    </div>
                    <div className={styles.r2}>
                      <div className={styles.desc}>
                        {t("admin.applicationAccepted")}
                      </div>
                      <div className={styles.date}>
                        {moment(verificationData?.data?.DecisionDate).format(
                          "DD.MM.yyyy | HH:mm"
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Accordion>
      </div>

      <div className={styles.history}>
        <div className={styles.header}>
          {t("metric.evidence")}
          <div
            onClick={(e) => {
              setIsEvidencesExpanded(!isEvidencesExpanded);
              e.stopPropagation();
            }}
            className={styles.expanderWrapper}
          >
            <FontAwesomeIcon
              className={classNames(styles.expander)}
              icon={isHistoryExpanded ? faCaretDown : faCaretUp}
            />
          </div>
        </div>
        <Accordion
          expanded={isEvidencesExpanded}
          setExpanded={setIsEvidencesExpanded}
        >
          <div className={styles.list}></div>
        </Accordion>
      </div>
    </div>
  );
};

export default MetricModal;
